import React, { useState, useEffect } from 'react';
import {
    Box,
    Paper,
    Typography,
    IconButton,
    Chip,
    Tooltip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    List,
    ListItem,
    ListItemText,
    Divider,
    Button,
    Snackbar,
    useMediaQuery,
    useTheme,
    Drawer,
    ListItemButton,
    Badge,
    ToggleButton,
    ToggleButtonGroup,
    Card,
    CardContent,
    Fade,
    Fab,
    AppBar,
    Toolbar,
} from '@mui/material';
import {
    ChevronLeft,
    ChevronRight,
    CalendarMonth,
    Close,
    AccessTime,
    Person,
    Email,
    Phone,
    Add,
    ViewWeek,
    Menu as MenuIcon,
    Today,
    Pets,
    Tune,
    Fullscreen,
    FullscreenExit,
    Keyboard,
    EventAvailable,
    CheckCircle,
    HourglassEmpty,
    Cancel as CancelIcon,
    Warning,
} from '@mui/icons-material';
import { useSocket } from '../contexts/SocketContext';
import CalendarHoverToolbar from '../components/CalendarHoverToolbar';
import AddReservationPopover from './AddReservationPopover';
import EditReservationPopover from './EditReservationPopover';

interface Reservation {
    id: string;
    customerName: string;
    customerEmail?: string;
    customerPhone?: string;
    startDate: string;
    endDate: string;
    status: 'pending' | 'confirmed' | 'cancelled';
    board: {
        id: string;
        name: string;
        type: string;
    };
    notes?: string;
}

interface Board {
    id: string;
    name: string;
    type: string;
}

interface ReservationsCalendarProps {
    reservations: Reservation[];
    boards: Board[];
    onReservationChange?: () => void;
}

type ViewMode = 'month' | 'week';
type CalendarMode = 'simple' | 'advanced';

const ReservationsCalendar: React.FC<ReservationsCalendarProps> = ({ reservations, boards, onReservationChange }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));
    const isMedium = useMediaQuery(theme.breakpoints.between('md', 'lg'));
    const isDark = theme.palette.mode === 'dark';

    const [localReservations, setLocalReservations] = useState<Reservation[]>(reservations);
    const [currentDate, setCurrentDate] = useState(new Date());
    const [selectedDate, setSelectedDate] = useState<Date | null>(null);
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [filters, setFilters] = useState({ confirmed: true, pending: true, cancelled: false });
    const [snackbar, setSnackbar] = useState<{ open: boolean; message: string }>({ open: false, message: '' });
    const [viewMode, setViewMode] = useState<ViewMode>('month');
    const [calendarMode, setCalendarMode] = useState<CalendarMode>('simple');
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [presentationMode, setPresentationMode] = useState(false);
    const [currentWeekStart, setCurrentWeekStart] = useState(() => {
        const now = new Date();
        const day = now.getDay();
        const diff = now.getDate() - day;
        return new Date(now.setDate(diff));
    });
    const [showKeyboardHelp, setShowKeyboardHelp] = useState(false);
    const socket = useSocket();

    // Popover States
    const [addPopoverAnchor, setAddPopoverAnchor] = useState<HTMLElement | null>(null);
    const [addPopoverDate, setAddPopoverDate] = useState<Date | null>(null);
    const [editPopoverAnchor, setEditPopoverAnchor] = useState<HTMLElement | null>(null);
    const [reservationToEdit, setReservationToEdit] = useState<Reservation | null>(null);

    // Sync local reservations with prop
    useEffect(() => {
        setLocalReservations(reservations);
    }, [reservations]);

    // Socket.io connection
    useEffect(() => {
        if (!socket) return;

        boards.forEach(board => {
            socket.emit('joinBoard', board.id);
        });

        const handleCreated = (newReservation: Reservation) => {
            setLocalReservations(prev => {
                if (prev.some(r => r.id === newReservation.id)) return prev;
                return [...prev, newReservation];
            });
        };

        const handleUpdated = (updatedReservation: Reservation) => {
            setLocalReservations(prev => prev.map(r => r.id === updatedReservation.id ? updatedReservation : r));
        };

        const handleCancelled = (cancelledReservation: Reservation) => {
            setLocalReservations(prev => prev.map(r => r.id === cancelledReservation.id ? cancelledReservation : r));
        };

        const handleDeleted = ({ id }: { id: string }) => {
            setLocalReservations(prev => prev.filter(r => r.id !== id));
        };

        socket.on('reservationCreated', handleCreated);
        socket.on('reservationUpdated', handleUpdated);
        socket.on('reservationCancelled', handleCancelled);
        socket.on('reservationDeleted', handleDeleted);

        return () => {
            socket.off('reservationCreated', handleCreated);
            socket.off('reservationUpdated', handleUpdated);
            socket.off('reservationCancelled', handleCancelled);
            socket.off('reservationDeleted', handleDeleted);
        };
    }, [socket, boards]);

    // Keyboard shortcuts (advanced mode only)
    useEffect(() => {
        if (calendarMode !== 'advanced') return;

        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;

            switch (e.key) {
                case 't':
                case 'T':
                    e.preventDefault();
                    goToToday();
                    break;
                case 'n':
                case 'N':
                    e.preventDefault();
                    const today = new Date();
                    setAddPopoverDate(today);
                    break;
                case 'ArrowLeft':
                    e.preventDefault();
                    viewMode === 'month' ? previousMonth() : previousWeek();
                    break;
                case 'ArrowRight':
                    e.preventDefault();
                    viewMode === 'month' ? nextMonth() : nextWeek();
                    break;
                case '?':
                    e.preventDefault();
                    setShowKeyboardHelp(true);
                    break;
                case 'Escape':
                    setShowKeyboardHelp(false);
                    setPresentationMode(false);
                    break;
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [calendarMode, viewMode]);

    const monthNames = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
    const dayNames = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];
    const dayNamesFull = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];

    const getCellHeight = () => {
        if (calendarMode === 'simple') {
            if (isMobile) return 72;
            if (isTablet) return 90;
            if (isMedium) return 100;
            return 110;
        }
        if (isMobile) return 60;
        if (isTablet) return 80;
        if (isMedium) return 90;
        return 100;
    };

    const getDaysInMonth = (date: Date) => {
        const year = date.getFullYear();
        const month = date.getMonth();
        const firstDay = new Date(year, month, 1);
        const lastDay = new Date(year, month + 1, 0);
        return { daysInMonth: lastDay.getDate(), startingDayOfWeek: firstDay.getDay() };
    };

    const toggleFilter = (status: keyof typeof filters) => {
        setFilters(prev => ({ ...prev, [status]: !prev[status] }));
    };

    const getReservationsForDay = (day: number) => {
        const year = currentDate.getFullYear();
        const month = currentDate.getMonth();
        const dayDate = new Date(year, month, day);
        return localReservations.filter(res => {
            if (!filters[res.status]) return false;
            const start = new Date(res.startDate);
            const end = new Date(res.endDate);
            const check = new Date(dayDate.getFullYear(), dayDate.getMonth(), dayDate.getDate());
            const s = new Date(start.getFullYear(), start.getMonth(), start.getDate());
            const e = new Date(end.getFullYear(), end.getMonth(), end.getDate());
            return check >= s && check <= e;
        });
    };

    const getReservationsForWeekDay = (date: Date) => {
        return localReservations.filter(res => {
            if (!filters[res.status]) return false;
            const start = new Date(res.startDate);
            const end = new Date(res.endDate);
            const check = new Date(date.getFullYear(), date.getMonth(), date.getDate());
            const s = new Date(start.getFullYear(), start.getMonth(), start.getDate());
            const e = new Date(end.getFullYear(), end.getMonth(), end.getDate());
            return check >= s && check <= e;
        });
    };

    const getTodayReservations = () => {
        const today = new Date();
        return getReservationsForDay(today.getDate()).filter(r => {
            const resDate = new Date(r.startDate);
            return resDate.getMonth() === today.getMonth() && resDate.getFullYear() === today.getFullYear();
        });
    };

    const getWeekStats = () => {
        const today = new Date();
        const weekStart = new Date(today);
        weekStart.setDate(today.getDate() - today.getDay());
        const weekEnd = new Date(weekStart);
        weekEnd.setDate(weekStart.getDate() + 6);

        const weekReservations = localReservations.filter(r => {
            const start = new Date(r.startDate);
            return start >= weekStart && start <= weekEnd && r.status !== 'cancelled';
        });

        const confirmed = weekReservations.filter(r => r.status === 'confirmed').length;
        const pending = weekReservations.filter(r => r.status === 'pending').length;

        return { total: weekReservations.length, confirmed, pending };
    };

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'confirmed': return 'success';
            case 'pending': return 'warning';
            case 'cancelled': return 'error';
            default: return 'default';
        }
    };

    const getStatusIcon = (status: string) => {
        switch (status) {
            case 'confirmed': return <CheckCircle sx={{ fontSize: 16 }} />;
            case 'pending': return <HourglassEmpty sx={{ fontSize: 16 }} />;
            case 'cancelled': return <CancelIcon sx={{ fontSize: 16 }} />;
            default: return undefined;
        }
    };

    const getStatusLabel = (status: string) => {
        switch (status) {
            case 'confirmed': return 'Confirmada';
            case 'pending': return 'Pendiente';
            case 'cancelled': return 'Cancelada';
            default: return status;
        }
    };

    const previousMonth = () => setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
    const nextMonth = () => setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));

    const goToToday = () => {
        setCurrentDate(new Date());
        const now = new Date();
        const day = now.getDay();
        const diff = now.getDate() - day;
        setCurrentWeekStart(new Date(now.setDate(diff)));
    };

    const previousWeek = () => {
        const newDate = new Date(currentWeekStart);
        newDate.setDate(newDate.getDate() - 7);
        setCurrentWeekStart(newDate);
        setCurrentDate(newDate);
    };

    const nextWeek = () => {
        const newDate = new Date(currentWeekStart);
        newDate.setDate(newDate.getDate() + 7);
        setCurrentWeekStart(newDate);
        setCurrentDate(newDate);
    };

    const handleDayClick = (day: number) => {
        const year = currentDate.getFullYear();
        const month = currentDate.getMonth();
        const date = new Date(year, month, day);
        setSelectedDate(date);
        setIsDialogOpen(true);
    };

    const handleWeekDayClick = (date: Date) => {
        setSelectedDate(date);
        setIsDialogOpen(true);
    };

    const handleAddClick = (event: React.MouseEvent<HTMLElement>, day: number) => {
        event.stopPropagation();
        const year = currentDate.getFullYear();
        const month = currentDate.getMonth();
        const date = new Date(year, month, day);
        setAddPopoverDate(date);
        setAddPopoverAnchor(event.currentTarget);
    };

    const handleAddWeekClick = (event: React.MouseEvent<HTMLElement>, date: Date) => {
        event.stopPropagation();
        setAddPopoverDate(date);
        setAddPopoverAnchor(event.currentTarget);
    };

    const handleAddSuccess = () => {
        setSnackbar({ open: true, message: 'Reserva creada exitosamente' });
        if (onReservationChange) onReservationChange();
    };

    const handleEditClick = (event: React.MouseEvent<HTMLElement>, reservation: Reservation) => {
        event.stopPropagation();
        setReservationToEdit(reservation);
        setEditPopoverAnchor(event.currentTarget);
    };

    const handleEditSuccess = () => {
        setSnackbar({ open: true, message: 'Reserva actualizada exitosamente' });
        if (onReservationChange) onReservationChange();
    };

    const handleCloseDialog = () => {
        setIsDialogOpen(false);
        setSelectedDate(null);
    };

    const handleCloseSnackbar = () => {
        setSnackbar({ ...snackbar, open: false });
    };

    const cancelReservation = async (id: string) => {
        try {
            const API_URL = process.env.REACT_APP_API_URL || 'https://api.turn.ar';
            const res = await fetch(`${API_URL}/api/reservations/${id}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
            });
            if (!res.ok) throw new Error('Failed to cancel reservation');
            setSnackbar({ open: true, message: 'Reserva cancelada' });
            if (onReservationChange) onReservationChange();
        } catch (e) {
            console.error(e);
            setSnackbar({ open: true, message: 'Error al cancelar' });
        }
    };

    const handleDragStart = (e: React.DragEvent, reservationId: string) => {
        if (calendarMode === 'simple') return;
        e.dataTransfer.setData('reservationId', reservationId);
        e.dataTransfer.effectAllowed = 'move';
    };

    const handleDragOver = (e: React.DragEvent) => {
        if (calendarMode === 'simple') return;
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
    };

    const handleDrop = async (e: React.DragEvent, targetDay: number) => {
        if (calendarMode === 'simple') return;
        e.preventDefault();
        const reservationId = e.dataTransfer.getData('reservationId');
        const reservation = localReservations.find(r => r.id === reservationId);
        if (!reservation) return;

        const year = currentDate.getFullYear();
        const month = currentDate.getMonth();
        const newStartDate = new Date(year, month, targetDay);
        const originalStart = new Date(reservation.startDate);
        newStartDate.setHours(originalStart.getHours(), originalStart.getMinutes());

        const originalEnd = new Date(reservation.endDate);
        const duration = originalEnd.getTime() - originalStart.getTime();
        const newEndDate = new Date(newStartDate.getTime() + duration);

        try {
            const API_URL = process.env.REACT_APP_API_URL || 'https://api.turn.ar';
            const res = await fetch(`${API_URL}/api/reservations/${reservationId}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                },
                body: JSON.stringify({
                    ...reservation,
                    startDate: newStartDate.toISOString(),
                    endDate: newEndDate.toISOString(),
                    boardId: reservation.board.id
                })
            });
            if (!res.ok) throw new Error('Failed to move reservation');
            setSnackbar({ open: true, message: 'Reserva movida exitosamente' });
            if (onReservationChange) onReservationChange();
        } catch (err) {
            console.error(err);
            setSnackbar({ open: true, message: 'Error al mover la reserva' });
        }
    };

    const formatTime = (dateString: string) => new Date(dateString).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

    const { daysInMonth, startingDayOfWeek } = getDaysInMonth(currentDate);
    const days = Array.from({ length: daysInMonth }, (_, i) => i + 1);
    const emptyDays = Array.from({ length: startingDayOfWeek }, (_, i) => i);

    const weekDays = Array.from({ length: 7 }, (_, i) => {
        const date = new Date(currentWeekStart);
        date.setDate(date.getDate() + i);
        return date;
    });

    const selectedDateReservations = selectedDate ? getReservationsForDay(selectedDate.getDate()) : [];
    const selectedWeekDayReservations = selectedDate ? getReservationsForWeekDay(selectedDate) : [];

    const cellHeight = getCellHeight();
    const todayReservations = getTodayReservations();
    const weekStats = getWeekStats();

    // === PRESENTATION MODE ===
    if (presentationMode) {
        const today = new Date();
        const todayRes = localReservations.filter(r => {
            const start = new Date(r.startDate);
            return start.getDate() === today.getDate() &&
                start.getMonth() === today.getMonth() &&
                start.getFullYear() === today.getFullYear() &&
                r.status !== 'cancelled';
        }).sort((a, b) => new Date(a.startDate).getTime() - new Date(b.startDate).getTime());

        return (
            <Box sx={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                zIndex: 9999,
                bgcolor: isDark ? '#121212' : '#F5F5F5',
                display: 'flex',
                flexDirection: 'column',
            }}>
                <AppBar position="static" sx={{ background: 'linear-gradient(135deg, #FF6B9D 0%, #9B59B6 100%)' }}>
                    <Toolbar>
                        <Pets sx={{ mr: 2 }} />
                        <Typography variant="h4" fontWeight="bold" sx={{ flex: 1 }}>
                            Agenda del día - {today.getDate()} de {monthNames[today.getMonth()]}
                        </Typography>
                        <IconButton color="inherit" onClick={() => setPresentationMode(false)}>
                            <FullscreenExit />
                        </IconButton>
                    </Toolbar>
                </AppBar>

                <Box sx={{ flex: 1, p: 4, overflow: 'auto' }}>
                    <Box sx={{ display: 'flex', gap: 4, mb: 4 }}>
                        <Card sx={{ minWidth: 200, borderRadius: 3, borderLeft: '4px solid #4caf50' }}>
                            <CardContent>
                                <Typography variant="h6" color="text.secondary">Confirmadas</Typography>
                                <Typography variant="h3" fontWeight="bold" color="success.main">
                                    {todayRes.filter(r => r.status === 'confirmed').length}
                                </Typography>
                            </CardContent>
                        </Card>
                        <Card sx={{ minWidth: 200, borderRadius: 3, borderLeft: '4px solid #ff9800' }}>
                            <CardContent>
                                <Typography variant="h6" color="text.secondary">Pendientes</Typography>
                                <Typography variant="h3" fontWeight="bold" color="warning.main">
                                    {todayRes.filter(r => r.status === 'pending').length}
                                </Typography>
                            </CardContent>
                        </Card>
                        <Card sx={{ minWidth: 200, borderRadius: 3, borderLeft: '4px solid #9B59B6' }}>
                            <CardContent>
                                <Typography variant="h6" color="text.secondary">Total</Typography>
                                <Typography variant="h3" fontWeight="bold" sx={{ color: '#9B59B6' }}>
                                    {todayRes.length}
                                </Typography>
                            </CardContent>
                        </Card>
                    </Box>

                    <Typography variant="h5" fontWeight="bold" sx={{ mb: 2 }}>
                        Próximas citas
                    </Typography>

                    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 2 }}>
                        {todayRes.map(res => (
                            <Card key={res.id} sx={{
                                borderRadius: 3,
                                borderLeft: `4px solid ${res.status === 'confirmed' ? '#4caf50' : '#ff9800'}`,
                                transition: 'transform 0.2s',
                                '&:hover': { transform: 'scale(1.02)' }
                            }}>
                                <CardContent>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                        <Typography variant="h5" fontWeight="bold" sx={{ color: '#9B59B6' }}>
                                            {formatTime(res.startDate)}
                                        </Typography>
                                        <Chip
                                            icon={getStatusIcon(res.status)}
                                            label={getStatusLabel(res.status)}
                                            color={getStatusColor(res.status)}
                                            size="small"
                                        />
                                    </Box>
                                    <Typography variant="h6" fontWeight="bold">{res.customerName}</Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        {res.board.name}
                                    </Typography>
                                    {res.customerPhone && (
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                                            <Phone sx={{ fontSize: 16, color: 'text.secondary' }} />
                                            <Typography variant="body2" color="text.secondary">{res.customerPhone}</Typography>
                                        </Box>
                                    )}
                                </CardContent>
                            </Card>
                        ))}
                        {todayRes.length === 0 && (
                            <Box sx={{ textAlign: 'center', py: 8, gridColumn: '1 / -1' }}>
                                <Typography variant="h5" color="text.secondary">
                                    No hay reservas para hoy
                                </Typography>
                            </Box>
                        )}
                    </Box>
                </Box>
            </Box>
        );
    }

    // === RENDER ===
    return (
        <>
            <Paper elevation={calendarMode === 'simple' ? 0 : 4} sx={{
                p: { xs: 1.5, sm: 2, md: 3 },
                background: isDark ? 'rgba(30,30,30,0.95)' : 'rgba(255,255,255,0.95)',
                backdropFilter: 'blur(20px)',
                borderRadius: { xs: 2, sm: 3 },
                boxShadow: calendarMode === 'simple'
                    ? '0 2px 12px rgba(0,0,0,0.08)'
                    : (isDark ? '0 8px 32px rgba(0,0,0,0.3)' : '0 8px 32px rgba(0,0,0,0.1)'),
                border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.06)'}`,
                mt: { xs: 1, sm: 3 },
                overflow: 'hidden'
            }}>
                {/* === HEADER === */}
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {isMobile && (
                            <IconButton onClick={() => setDrawerOpen(true)} size="small">
                                <MenuIcon />
                            </IconButton>
                        )}
                        <CalendarMonth sx={{ fontSize: { xs: 24, sm: 32 }, color: '#9B59B6' }} />
                        <Typography variant="h5" fontWeight="bold" className="cat-gradient-text" sx={{ fontSize: { xs: '1rem', sm: '1.5rem' } }}>
                            {viewMode === 'month' ? 'Calendario' : 'Semana'}
                        </Typography>
                    </Box>

                    {/* Navigation */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.5, sm: 1 }, flexWrap: 'wrap' }}>
                        <IconButton onClick={viewMode === 'month' ? previousMonth : previousWeek} size="small" sx={{ color: '#FF6B9D' }}>
                            <ChevronLeft />
                        </IconButton>
                        <Typography
                            variant={isMobile ? 'body2' : 'h6'}
                            sx={{
                                minWidth: { xs: 80, sm: 120, md: 180 },
                                textAlign: 'center',
                                color: isDark ? '#E0E0E0' : '#5D4037',
                                fontWeight: 'bold',
                                fontSize: { xs: '0.75rem', sm: '1rem' }
                            }}
                        >
                            {viewMode === 'month'
                                ? `${monthNames[currentDate.getMonth()]} ${currentDate.getFullYear()}`
                                : `${currentWeekStart.getDate()} - ${weekDays[6].getDate()} de ${monthNames[currentWeekStart.getMonth()].substring(0, 3)}`
                            }
                        </Typography>
                        <IconButton onClick={viewMode === 'month' ? nextMonth : nextWeek} size="small" sx={{ color: '#FF6B9D' }}>
                            <ChevronRight />
                        </IconButton>
                        <Button
                            variant={calendarMode === 'simple' ? 'contained' : 'outlined'}
                            size="small"
                            onClick={goToToday}
                            startIcon={<Today />}
                            sx={{
                                ml: 1,
                                ...(calendarMode === 'simple' && {
                                    background: 'linear-gradient(135deg, #FF6B9D 0%, #9B59B6 100%)',
                                    border: 'none',
                                    fontWeight: 'bold',
                                })
                            }}
                        >
                            {isMobile ? '' : 'Hoy'}
                        </Button>
                    </Box>

                    {/* Mode Toggle & Actions */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {!isMobile && (
                            <ToggleButtonGroup
                                value={viewMode}
                                exclusive
                                onChange={(_, v) => v && setViewMode(v)}
                                size="small"
                            >
                                <ToggleButton value="month">
                                    <CalendarMonth sx={{ mr: 0.5 }} fontSize="small" /> Mes
                                </ToggleButton>
                                <ToggleButton value="week">
                                    <ViewWeek sx={{ mr: 0.5 }} fontSize="small" /> Semana
                                </ToggleButton>
                            </ToggleButtonGroup>
                        )}

                        <Chip
                            icon={calendarMode === 'simple' ? <Pets /> : <Tune />}
                            label={calendarMode === 'simple' ? '😺 Simple' : '⚙️ Avanzado'}
                            onClick={() => setCalendarMode(calendarMode === 'simple' ? 'advanced' : 'simple')}
                            sx={{
                                cursor: 'pointer',
                                fontWeight: 'bold',
                                bgcolor: calendarMode === 'simple'
                                    ? (isDark ? '#2A1A20' : '#FFF5F8')
                                    : (isDark ? '#1A2A30' : '#E3F2FD'),
                                color: calendarMode === 'simple' ? '#FF6B9D' : '#2196F3',
                                border: `1px solid ${calendarMode === 'simple' ? '#FF6B9D' : '#2196F3'}`,
                                '&:hover': { opacity: 0.9 },
                                fontSize: { xs: '0.7rem', sm: '0.8125rem' },
                            }}
                        />

                        {calendarMode === 'advanced' && (
                            <>
                                <Tooltip title="Modo presentación">
                                    <IconButton onClick={() => setPresentationMode(true)} size="small" sx={{ color: '#9B59B6' }}>
                                        <Fullscreen />
                                    </IconButton>
                                </Tooltip>
                                <Tooltip title="Atajos de teclado (?)">
                                    <IconButton onClick={() => setShowKeyboardHelp(true)} size="small" sx={{ color: 'text.secondary' }}>
                                        <Keyboard />
                                    </IconButton>
                                </Tooltip>
                            </>
                        )}
                    </Box>
                </Box>

                {/* === DAY SUMMARY (Simple Mode) === */}
                {calendarMode === 'simple' && (
                    <Fade in>
                        <Card sx={{
                            mb: 2,
                            borderRadius: 3,
                            background: isDark
                                ? 'linear-gradient(135deg, rgba(155,89,182,0.15) 0%, rgba(255,107,157,0.1) 100%)'
                                : 'linear-gradient(135deg, rgba(155,89,182,0.08) 0%, rgba(255,107,157,0.05) 100%)',
                            border: `1px solid ${isDark ? 'rgba(155,89,182,0.3)' : 'rgba(155,89,182,0.15)'}`,
                        }}>
                            <CardContent sx={{ py: { xs: 1.5, sm: 2 } }}>
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: { xs: 2, sm: 3 }, alignItems: 'center', justifyContent: 'center' }}>
                                    <Box sx={{ textAlign: 'center' }}>
                                        <Typography variant="body2" color="text.secondary" fontWeight="bold">Hoy</Typography>
                                        <Typography variant="h5" fontWeight="bold" sx={{ color: '#9B59B6' }}>
                                            {todayReservations.length}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">reservas</Typography>
                                    </Box>
                                    <Divider orientation="vertical" flexItem />
                                    <Box sx={{ display: 'flex', gap: 2 }}>
                                        <Box sx={{ textAlign: 'center' }}>
                                            <Chip icon={<CheckCircle sx={{ fontSize: 14 }} />} label={todayReservations.filter(r => r.status === 'confirmed').length} color="success" size="small" sx={{ fontWeight: 'bold' }} />
                                            <Typography variant="caption" display="block" color="text.secondary">Confirmadas</Typography>
                                        </Box>
                                        <Box sx={{ textAlign: 'center' }}>
                                            <Chip icon={<HourglassEmpty sx={{ fontSize: 14 }} />} label={todayReservations.filter(r => r.status === 'pending').length} color="warning" size="small" sx={{ fontWeight: 'bold' }} />
                                            <Typography variant="caption" display="block" color="text.secondary">Pendientes</Typography>
                                        </Box>
                                    </Box>
                                    <Divider orientation="vertical" flexItem />
                                    <Box sx={{ textAlign: 'center' }}>
                                        <Typography variant="body2" color="text.secondary" fontWeight="bold">Esta semana</Typography>
                                        <Typography variant="h6" fontWeight="bold" sx={{ color: '#4ECDC4' }}>
                                            {weekStats.total} reservas
                                        </Typography>
                                    </Box>
                                </Box>
                            </CardContent>
                        </Card>
                    </Fade>
                )}

                {/* === FILTERS (Advanced Mode) === */}
                {calendarMode === 'advanced' && (
                    <Box sx={{ display: 'flex', gap: 1, mb: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
                        <Chip
                            icon={<CheckCircle sx={{ fontSize: 14 }} />}
                            label="Confirmada"
                            size="small"
                            color="success"
                            variant={filters.confirmed ? 'filled' : 'outlined'}
                            onClick={() => toggleFilter('confirmed')}
                            sx={{ cursor: 'pointer', opacity: filters.confirmed ? 1 : 0.6 }}
                        />
                        <Chip
                            icon={<HourglassEmpty sx={{ fontSize: 14 }} />}
                            label="Pendiente"
                            size="small"
                            color="warning"
                            variant={filters.pending ? 'filled' : 'outlined'}
                            onClick={() => toggleFilter('pending')}
                            sx={{ cursor: 'pointer', opacity: filters.pending ? 1 : 0.6 }}
                        />
                        <Chip
                            icon={<CancelIcon sx={{ fontSize: 14 }} />}
                            label="Cancelada"
                            size="small"
                            color="error"
                            variant={filters.cancelled ? 'filled' : 'outlined'}
                            onClick={() => toggleFilter('cancelled')}
                            sx={{ cursor: 'pointer', opacity: filters.cancelled ? 1 : 0.6 }}
                        />
                    </Box>
                )}

                {/* === MONTH VIEW === */}
                {viewMode === 'month' ? (
                    <>
                        {/* Day names */}
                        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 0.5, mb: 1 }}>
                            {dayNames.map(day => (
                                <Box key={day} sx={{
                                    textAlign: 'center',
                                    fontWeight: 'bold',
                                    color: 'text.secondary',
                                    py: { xs: 0.5, sm: 1 },
                                    fontSize: { xs: '0.65rem', sm: '0.875rem' }
                                }}>{day}</Box>
                            ))}
                        </Box>

                        {/* Calendar grid */}
                        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: { xs: 0.25, sm: 0.5 } }}>
                            {emptyDays.map((_, idx) => (<Box key={`empty-${idx}`} sx={{ height: cellHeight }} />))}
                            {days.map(day => {
                                const dayReservations = getReservationsForDay(day);
                                const today = new Date();
                                const isToday = day === today.getDate() && currentDate.getMonth() === today.getMonth() && currentDate.getFullYear() === today.getFullYear();
                                const confirmedCount = dayReservations.filter(r => r.status === 'confirmed').length;
                                const pendingCount = dayReservations.filter(r => r.status === 'pending').length;

                                return (
                                    <Paper
                                        key={day}
                                        elevation={isToday ? 3 : (calendarMode === 'simple' ? 0 : 1)}
                                        onClick={() => handleDayClick(day)}
                                        onDragOver={handleDragOver}
                                        onDrop={(e) => handleDrop(e, day)}
                                        sx={{
                                            height: cellHeight,
                                            p: { xs: 0.5, sm: 1 },
                                            position: 'relative',
                                            border: isToday ? '2px solid' : '1px solid',
                                            borderColor: isToday ? '#FF6B9D' : 'divider',
                                            backgroundColor: isToday
                                                ? (isDark ? 'rgba(255,107,157,0.08)' : 'rgba(255,107,157,0.04)')
                                                : 'background.paper',
                                            overflow: 'hidden',
                                            cursor: 'pointer',
                                            borderRadius: 1,
                                            '&:hover': {
                                                boxShadow: calendarMode === 'simple' ? 1 : 2,
                                                borderColor: '#FF6B9D'
                                            }
                                        }}
                                    >
                                        {calendarMode === 'advanced' && (
                                            <CalendarHoverToolbar
                                                onAdd={(e) => handleAddClick(e, day)}
                                                onEdit={() => handleDayClick(day)}
                                                onCancel={() => dayReservations.length > 0 && cancelReservation(dayReservations[0].id)}
                                                advancedMode={true}
                                                reservationName={dayReservations[0]?.customerName}
                                            />
                                        )}

                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.25 }}>
                                            <Typography
                                                variant="body2"
                                                fontWeight={isToday ? 'bold' : 'normal'}
                                                color={isToday ? '#FF6B9D' : 'text.primary'}
                                                sx={{
                                                    fontSize: { xs: calendarMode === 'simple' ? '0.85rem' : '0.7rem', sm: calendarMode === 'simple' ? '1rem' : '0.875rem' },
                                                    fontWeight: isToday ? 800 : (calendarMode === 'simple' ? 600 : 400),
                                                }}
                                            >
                                                {day}
                                            </Typography>
                                            {dayReservations.length > 0 && (
                                                <Box sx={{ display: 'flex', gap: 0.25 }}>
                                                    {confirmedCount > 0 && (
                                                        <Badge badgeContent={confirmedCount} color="success"
                                                            sx={{ '& .MuiBadge-badge': { fontSize: '0.55rem', height: 14, minWidth: 14 } }}>
                                                            <Box />
                                                        </Badge>
                                                    )}
                                                    {pendingCount > 0 && (
                                                        <Badge badgeContent={pendingCount} color="warning"
                                                            sx={{ '& .MuiBadge-badge': { fontSize: '0.55rem', height: 14, minWidth: 14 } }}>
                                                            <Box />
                                                        </Badge>
                                                    )}
                                                </Box>
                                            )}
                                        </Box>

                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, overflow: 'hidden' }}>
                                            {dayReservations.slice(0, isMobile ? 1 : (calendarMode === 'simple' ? 2 : 2)).map(res => (
                                                <Tooltip key={res.id} title={`${res.board.name} - ${res.customerName} (${getStatusLabel(res.status)})`} arrow>
                                                    <div
                                                        draggable={calendarMode === 'advanced'}
                                                        onDragStart={(e) => handleDragStart(e, res.id)}
                                                        style={{ cursor: calendarMode === 'advanced' ? 'grab' : 'pointer' }}
                                                    >
                                                        <Chip
                                                            label={isMobile ? res.customerName.substring(0, 6) + '...' : res.board.name}
                                                            size="small"
                                                            color={getStatusColor(res.status)}
                                                            onClick={(e) => handleEditClick(e, res)}
                                                            icon={calendarMode === 'simple' ? getStatusIcon(res.status) : undefined}
                                                            sx={{
                                                                fontSize: { xs: '0.5rem', sm: calendarMode === 'simple' ? '0.7rem' : '0.65rem' },
                                                                height: { xs: 18, sm: calendarMode === 'simple' ? 24 : 20 },
                                                                width: '100%',
                                                                pointerEvents: 'none',
                                                                '& .MuiChip-label': { px: 0.25, overflow: 'hidden', textOverflow: 'ellipsis', display: 'block' },
                                                                '& .MuiChip-icon': { ml: 0.5 }
                                                            }}
                                                        />
                                                    </div>
                                                </Tooltip>
                                            ))}
                                            {!isMobile && dayReservations.length > 2 && (
                                                <Typography variant="caption" sx={{
                                                    fontSize: '0.6rem',
                                                    color: 'text.secondary',
                                                    textAlign: 'center',
                                                    fontWeight: calendarMode === 'simple' ? 'bold' : 'normal'
                                                }}>
                                                    +{dayReservations.length - 2} más
                                                </Typography>
                                            )}
                                        </Box>

                                        {/* Simple mode: add button on empty days */}
                                        {calendarMode === 'simple' && dayReservations.length === 0 && !isMobile && (
                                            <Box
                                                onClick={(e) => handleAddClick(e, day)}
                                                sx={{
                                                    position: 'absolute',
                                                    bottom: 4,
                                                    right: 4,
                                                    width: 24,
                                                    height: 24,
                                                    borderRadius: '50%',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    bgcolor: isDark ? 'rgba(155,89,182,0.2)' : 'rgba(155,89,182,0.1)',
                                                    color: '#9B59B6',
                                                    cursor: 'pointer',
                                                    opacity: 0,
                                                    transition: 'opacity 0.2s',
                                                    '&:hover': { bgcolor: isDark ? 'rgba(155,89,182,0.4)' : 'rgba(155,89,182,0.2)' },
                                                    '.MuiPaper-root:hover &': { opacity: 1 },
                                                }}
                                            >
                                                <Add sx={{ fontSize: 16 }} />
                                            </Box>
                                        )}
                                    </Paper>
                                );
                            })}
                        </Box>
                    </>
                ) : (
                    // === WEEK VIEW (Advanced Mode) ===
                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                        {/* Week header */}
                        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 0.5, mb: 1 }}>
                            {weekDays.map((date, idx) => {
                                const today = new Date();
                                const isToday = date.getDate() === today.getDate() && date.getMonth() === today.getMonth() && date.getFullYear() === today.getFullYear();
                                const dayRes = getReservationsForWeekDay(date);
                                return (
                                    <Box
                                        key={idx}
                                        onClick={() => handleWeekDayClick(date)}
                                        sx={{
                                            textAlign: 'center',
                                            p: 1,
                                            borderRadius: 1,
                                            bgcolor: isToday ? (isDark ? 'rgba(255,107,157,0.15)' : 'rgba(255,107,157,0.08)') : 'transparent',
                                            cursor: 'pointer',
                                            '&:hover': { bgcolor: isToday ? (isDark ? 'rgba(255,107,157,0.2)' : 'rgba(255,107,157,0.12)') : (isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)') }
                                        }}
                                    >
                                        <Typography variant="caption" sx={{ fontWeight: 'bold', display: 'block', color: isToday ? '#FF6B9D' : 'text.secondary' }}>
                                            {dayNames[idx]}
                                        </Typography>
                                        <Typography variant="h6" sx={{ fontWeight: isToday ? 'bold' : 'normal', color: isToday ? '#FF6B9D' : 'text.primary' }}>
                                            {date.getDate()}
                                        </Typography>
                                        {dayRes.length > 0 && (
                                            <Chip label={dayRes.length} size="small" color="primary" sx={{ mt: 0.5, height: 16, fontSize: '0.65rem' }} />
                                        )}
                                    </Box>
                                );
                            })}
                        </Box>

                        {/* Week day cells */}
                        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 0.5 }}>
                            {weekDays.map((date, idx) => {
                                const dayRes = getReservationsForWeekDay(date);
                                const today = new Date();
                                const isToday = date.getDate() === today.getDate() && date.getMonth() === today.getMonth() && date.getFullYear() === today.getFullYear();
                                return (
                                    <Paper
                                        key={idx}
                                        elevation={isToday ? 2 : 1}
                                        onClick={() => handleWeekDayClick(date)}
                                        onDragOver={handleDragOver}
                                        onDrop={(e) => handleDrop(e, date.getDate())}
                                        sx={{
                                            minHeight: { xs: 150, sm: 200, md: 250 },
                                            p: 0.5,
                                            position: 'relative',
                                            border: isToday ? '2px solid' : '1px solid',
                                            borderColor: isToday ? '#FF6B9D' : 'divider',
                                            cursor: 'pointer',
                                            '&:hover': { boxShadow: 2 }
                                        }}
                                    >
                                        {calendarMode === 'advanced' && (
                                            <CalendarHoverToolbar
                                                onAdd={(e) => handleAddWeekClick(e, date)}
                                                onEdit={() => handleWeekDayClick(date)}
                                                onCancel={() => { }}
                                                advancedMode={true}
                                            />
                                        )}
                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mt: 2 }}>
                                            {dayRes.map(res => (
                                                <Tooltip key={res.id} title={`${res.customerName} - ${formatTime(res.startDate)} (${getStatusLabel(res.status)})`} arrow>
                                                    <Paper
                                                        draggable={calendarMode === 'advanced'}
                                                        onDragStart={(e) => handleDragStart(e, res.id)}
                                                        onClick={(e) => handleEditClick(e, res)}
                                                        sx={{
                                                            p: 0.5,
                                                            bgcolor: res.status === 'confirmed' ? (isDark ? 'rgba(76,175,80,0.2)' : 'success.light') :
                                                                res.status === 'pending' ? (isDark ? 'rgba(255,152,0,0.2)' : 'warning.light') :
                                                                    (isDark ? 'rgba(244,67,54,0.2)' : 'error.light'),
                                                            borderRadius: 0.5,
                                                            cursor: calendarMode === 'advanced' ? 'grab' : 'pointer',
                                                            fontSize: { xs: '0.65rem', sm: '0.75rem' }
                                                        }}
                                                    >
                                                        <Typography variant="caption" sx={{ fontWeight: 'bold', display: 'block' }}>
                                                            {formatTime(res.startDate)}
                                                        </Typography>
                                                        <Typography variant="caption" sx={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                            {res.customerName}
                                                        </Typography>
                                                    </Paper>
                                                </Tooltip>
                                            ))}
                                        </Box>
                                    </Paper>
                                );
                            })}
                        </Box>
                    </Box>
                )}
            </Paper>

            {/* === FLOATING ADD BUTTON (Simple Mode Mobile) === */}
            {calendarMode === 'simple' && isMobile && (
                <Fab
                    color="primary"
                    onClick={(e) => {
                        const today = new Date();
                        setAddPopoverDate(today);
                        setAddPopoverAnchor(e.currentTarget);
                    }}
                    sx={{
                        position: 'fixed',
                        bottom: 80,
                        right: 16,
                        background: 'linear-gradient(135deg, #FF6B9D 0%, #9B59B6 100%)',
                        zIndex: 100,
                    }}
                >
                    <Add />
                </Fab>
            )}

            {/* === MOBILE DRAWER === */}
            <Drawer anchor="bottom" open={drawerOpen} onClose={() => setDrawerOpen(false)}>
                <Box sx={{ p: 2 }}>
                    <Typography variant="h6" gutterBottom>Opciones</Typography>
                    <List>
                        <ListItemButton onClick={() => { setViewMode('month'); setDrawerOpen(false); }}>
                            <CalendarMonth sx={{ mr: 2, color: '#9B59B6' }} />
                            <ListItemText primary="Vista de Mes" />
                        </ListItemButton>
                        <ListItemButton onClick={() => { setViewMode('week'); setDrawerOpen(false); }}>
                            <ViewWeek sx={{ mr: 2, color: '#9B59B6' }} />
                            <ListItemText primary="Vista de Semana" />
                        </ListItemButton>
                        <Divider sx={{ my: 1 }} />
                        <ListItemButton onClick={() => { setCalendarMode(calendarMode === 'simple' ? 'advanced' : 'simple'); setDrawerOpen(false); }}>
                            {calendarMode === 'simple' ? <Tune sx={{ mr: 2, color: '#2196F3' }} /> : <Pets sx={{ mr: 2, color: '#FF6B9D' }} />}
                            <ListItemText
                                primary={calendarMode === 'simple' ? 'Modo Avanzado' : 'Modo Simple'}
                                secondary={calendarMode === 'simple' ? 'Más herramientas' : 'Vista simplificada'}
                            />
                        </ListItemButton>
                        {calendarMode === 'advanced' && (
                            <>
                                <ListItemButton onClick={() => { setPresentationMode(true); setDrawerOpen(false); }}>
                                    <Fullscreen sx={{ mr: 2, color: '#9B59B6' }} />
                                    <ListItemText primary="Modo Presentación" />
                                </ListItemButton>
                            </>
                        )}
                    </List>
                </Box>
            </Drawer>

            {/* === DAY DETAIL DIALOG === */}
            <Dialog open={isDialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
                <DialogTitle sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    pb: 1,
                    background: isDark ? 'rgba(155,89,182,0.1)' : 'rgba(155,89,182,0.05)',
                }}>
                    <Box>
                        <Typography variant="h5" fontWeight="bold" sx={{ color: '#9B59B6' }}>
                            {selectedDate?.getDate()} de {selectedDate && monthNames[selectedDate.getMonth()]}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            {dayNamesFull[selectedDate?.getDay() || 0]} - {selectedWeekDayReservations.length} reserva(s)
                        </Typography>
                    </Box>
                    <IconButton onClick={handleCloseDialog} size="small"><Close /></IconButton>
                </DialogTitle>
                <Divider />
                <DialogContent sx={{ pt: 2 }}>
                    {selectedWeekDayReservations.length > 0 ? (
                        <List disablePadding>
                            {selectedWeekDayReservations.map((res, idx) => (
                                <React.Fragment key={res.id}>
                                    <ListItem alignItems="flex-start" sx={{
                                        px: 0,
                                        py: 1.5,
                                        flexDirection: 'column',
                                        gap: 1,
                                        borderRadius: 2,
                                        '&:hover': { bgcolor: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)' }
                                    }}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'flex-start' }}>
                                            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                                                <Chip label={res.board.name} size="small" variant="outlined" color="primary" />
                                                <Chip
                                                    icon={getStatusIcon(res.status)}
                                                    label={getStatusLabel(res.status)}
                                                    size="small"
                                                    color={getStatusColor(res.status)}
                                                />
                                            </Box>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'text.secondary' }}>
                                                <AccessTime fontSize="small" />
                                                <Typography variant="body2">{formatTime(res.startDate)} - {formatTime(res.endDate)}</Typography>
                                            </Box>
                                        </Box>
                                        <Box sx={{ width: '100%', pl: 1, borderLeft: '3px solid', borderColor: res.status === 'confirmed' ? 'success.main' : res.status === 'pending' ? 'warning.main' : 'error.main' }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                                <Person fontSize="small" color="action" />
                                                <Typography variant="subtitle2" fontWeight="bold">{res.customerName}</Typography>
                                            </Box>
                                            {res.customerEmail && (
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                                    <Email fontSize="small" color="action" sx={{ fontSize: 16 }} />
                                                    <Typography variant="body2" color="text.secondary">{res.customerEmail}</Typography>
                                                </Box>
                                            )}
                                            {res.customerPhone && (
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <Phone fontSize="small" color="action" sx={{ fontSize: 16 }} />
                                                    <Typography variant="body2" color="text.secondary">{res.customerPhone}</Typography>
                                                </Box>
                                            )}
                                            {res.notes && (
                                                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, fontStyle: 'italic' }}>
                                                    {res.notes}
                                                </Typography>
                                            )}
                                        </Box>
                                    </ListItem>
                                    {idx < selectedWeekDayReservations.length - 1 && <Divider />}
                                </React.Fragment>
                            ))}
                        </List>
                    ) : (
                        <Box sx={{ py: 4, textAlign: 'center', color: 'text.secondary' }}>
                            <EventAvailable sx={{ fontSize: 48, mb: 1, opacity: 0.5 }} />
                            <Typography variant="h6">No hay reservas para este día</Typography>
                            <Typography variant="body2" sx={{ mt: 1 }}>
                                Toca el botón + para agregar una nueva reserva
                            </Typography>
                            <Button
                                variant="contained"
                                startIcon={<Add />}
                                onClick={(e) => {
                                    handleCloseDialog();
                                    if (selectedDate) {
                                        setAddPopoverDate(selectedDate);
                                        setAddPopoverAnchor(e.currentTarget);
                                    }
                                }}
                                sx={{
                                    mt: 2,
                                    background: 'linear-gradient(135deg, #FF6B9D 0%, #9B59B6 100%)',
                                }}
                            >
                                Nueva Reserva
                            </Button>
                        </Box>
                    )}
                </DialogContent>
            </Dialog>

            {/* === KEYBOARD SHORTCUTS DIALOG === */}
            <Dialog open={showKeyboardHelp} onClose={() => setShowKeyboardHelp(false)} maxWidth="xs" fullWidth>
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Keyboard /> Atajos de Teclado
                </DialogTitle>
                <DialogContent>
                    <List dense>
                        <ListItem><ListItemText primary="T" secondary="Ir a hoy" /></ListItem>
                        <ListItem><ListItemText primary="N" secondary="Nueva reserva" /></ListItem>
                        <ListItem><ListItemText primary="← / →" secondary="Mes/semana anterior / siguiente" /></ListItem>
                        <ListItem><ListItemText primary="?" secondary="Mostrar esta ayuda" /></ListItem>
                        <ListItem><ListItemText primary="Esc" secondary="Cerrar diálogos" /></ListItem>
                    </List>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setShowKeyboardHelp(false)}>Cerrar</Button>
                </DialogActions>
            </Dialog>

            {/* === POPOVERS === */}
            <AddReservationPopover
                anchorEl={addPopoverAnchor}
                open={Boolean(addPopoverAnchor)}
                onClose={() => setAddPopoverAnchor(null)}
                onAddSuccess={handleAddSuccess}
                initialDate={addPopoverDate}
                boards={boards}
                existingReservations={localReservations}
            />

            <EditReservationPopover
                anchorEl={editPopoverAnchor}
                open={Boolean(editPopoverAnchor)}
                onClose={() => setEditPopoverAnchor(null)}
                onEditSuccess={handleEditSuccess}
                reservation={reservationToEdit}
                boards={boards}
                existingReservations={localReservations}
            />

            <Snackbar open={snackbar.open} autoHideDuration={3000} onClose={handleCloseSnackbar} message={snackbar.message} />
        </>
    );
};

export default ReservationsCalendar;
